import {
  ACTION,
  COMPONENT,
  CONFIG_OPTION,
  FORBIDDEN,
  FUNCTION,
  OPTIONAL,
  REQUIRES,
  STRING,
} from './constants';
import { assert } from '@ember/debug';
import { doesNotExist, exists, parseArgumentName } from './helpers';

/**
 * selectConfigOption verifies that the selected value is present in the set of options
 * provided. if it is not, then a fallback value is returned
 * @param {Array<string>} options
 * @param {string} selected
 * @param {string} fallback
 * @returns {string}
 */
export function selectConfigOption(options, selected, fallback) {
  return options.find(option => option === selected) || fallback;
}

/**
 * buildConfigOptionData constructs validation data for config option validations
 * @param {string} name the argument name
 * @param {object} item the validation item
 * @returns {object} the config option validation data
 */
function buildConfigOptionData(name, item) {
  return { name, options: item.options };
}

/**
 * Normalizes the when value to always be an array or undefined
 * @param {*} when the when value to normalize
 * @returns {Array|undefined} normalized when array or undefined
 */
function normalizeWhenValue(when) {
  if (when === undefined) return undefined;
  return Array.isArray(when) ? when : [when];
}

/**
 * Builds the when clause for the forbidden message
 * @param {Array|undefined} normalizedWhen the normalized when array
 * @returns {string} the when clause for the message
 */
function buildWhenClause(normalizedWhen) {
  if (normalizedWhen === undefined) return ' is provided';
  return ` is ${normalizedWhen.map(v => JSON.stringify(v)).join(' or ')}`;
}

/**
 * buildForbiddenData constructs validation data for forbidden argument validations
 * @param {string} name the argument name
 * @param {object} args the arguments object
 * @param {string} component the component name
 * @param {object} item the validation item
 * @param {object} componentInstance the component instance
 * @returns {object} the forbidden validation data
 */
function buildForbiddenData({ name, args, component, item, componentInstance }) {
  const normalizedWhen = normalizeWhenValue(item.when);
  const whenClause = buildWhenClause(normalizedWhen);
  let instanceId = componentInstance?.id ? ` [instance: ${componentInstance.id}]` : '';

  let forbiddenData = {
    message: `@${name} argument of ${component} is not allowed when @${item.forbiddingArg}${whenClause} because ${item.reason}${instanceId}`,
  };

  // ensure legacy validations without `forbiddingArg` are still supported
  if (exists(item.forbiddingArg)) {
    forbiddenData.forbiddingArgValue = parseArgumentName(args, item.forbiddingArg);
  }

  if (normalizedWhen !== undefined) {
    forbiddenData.when = normalizedWhen;
  }

  return forbiddenData;
}

/**
 * buildRequiresData constructs validation data for required dependency validations
 * @param {string} name the argument name
 * @param {object} args the arguments object
 * @param {string} component the component name
 * @param {object} item the validation item
 * @param {object} componentInstance the component instance to check for property values
 * @returns {object} the requires validation data
 */
function buildRequiresData({ name, args, component, item, componentInstance }) {
  let instanceId = componentInstance?.id ? ` [instance: ${componentInstance.id}]` : '';

  return {
    message: `@${item.name} argument of ${component} is required when @${name} is provided because ${item.reason}${instanceId}`,
    requiringArgValue: parseArgumentName(args, name),
    // Check the component instance property (getter) instead of just args for the required value
    // This allows defaults from metadata to be recognized by validation
    requiredArgValue: parseArgumentName(args, item.name) ?? componentInstance?.[item.name],
  };
}

/** discoverType determines the effective type for validation
 * @param {string} type the provided type
 * @returns {string} the effective type for validation
 */
function discoverType(type) {
  // both ACTION and COMPONENT types are validated as FUNCTION
  // due to JS classes being considered functions
  return [ACTION, COMPONENT].includes(type) ? FUNCTION : type;
}

/**
 * buildTypeData constructs validation data for type validations
 * @param {string} name the argument name
 * @param {object} args the arguments object
 * @param {string} component the component name
 * @param {string} listName the validation list name
 * @param {object} list the validation list
 * @param {object} componentInstance the component instance
 * @returns {object} the type validation data
 */
function buildTypeData({ name, args, component, listName, list, componentInstance }) {
  let { type } = list;
  let messageBase = `argument of ${component} must have a type of "${type}"${listName === OPTIONAL ? ' or be empty' : ''}`;
  let instanceId = componentInstance?.id ? ` [instance: ${componentInstance.id}]` : '';
  let debugHint = `\n\n💡 Debug: Search for <${component.replace(/::/g, '/')} @${name}=...> in templates to find the source.`;

  return {
    message: `@${name} ${messageBase}. Received ${args[name]}${instanceId}${debugHint}`,
    type: discoverType(type),
  };
}

/**
 * getValidationData constructs the validation data object for a given argument
 * @param {Array<object>} list the validation list
 * @param {string} listName the name of the validation list
 * @param {object} args the arguments object
 * @param {string} component the name of the component
 * @param {object|string} item the item to validate
 * @param {object} componentInstance the component instance
 * @returns {object} the constructed validation data object
 */
function getValidationData({ list, listName, args, component, item, componentInstance }) {
  let name = typeof item === STRING ? item : item.name;
  let validationData = { argument: parseArgumentName(args, name), component };
  let additionalData;

  if (listName === FORBIDDEN) {
    additionalData = buildForbiddenData({ name, args, component, item, componentInstance });
  } else if (listName === REQUIRES) {
    additionalData = buildRequiresData({
      name: item.requiringArg,
      args,
      component,
      item,
      componentInstance,
    });
  } else if (list.type === CONFIG_OPTION) {
    additionalData = buildConfigOptionData(name, item);
  } else {
    additionalData = buildTypeData({ name, args, component, listName, list, componentInstance });
  }

  return Object.assign(validationData, additionalData);
}

/**
 * validateArgumentList traverses the supplied argumentList and runs all applicable validations against
 * the args object, ensuring that the values in args are valid
 * @param {object} param0 with attributes args {object}, argumentList {array}, component {string}, and componentInstance {object}
 */
export function validateArgumentList({ args = {}, argumentList, component, componentInstance }) {
  // can't properly compose validation messages without component
  // this should be a _very_ rare case, as `validateArguments` falls back to `constructor.name`
  // when no componentName is provided
  assert(
    'component name must be present to properly compose error messages while validating components',
    typeof component === STRING && component.length > 0
  );
  // this is to ensure the `arguments` array is properly added to the validating component
  assert(
    `argumentList must be an array to properly validate arguments for ${component}`,
    Array.isArray(argumentList)
  );

  argumentList.forEach(list => {
    // iterate over the list of validations and use the list name (forbidden, optional, required)
    // to determine the validation to use for items in the list
    assert(`argumentList of ${component} should have validations defined`, !!list.validations);
    assert(`argumentList of ${component} should have assertions defined`, !!list.assertions);

    Object.keys(list.validations).forEach(listName => {
      let validationsList = list.validations[listName];

      if (doesNotExist(validationsList)) return;

      validationsList.forEach(item => {
        list.assertions[listName]?.(
          getValidationData({ list, listName, args, component, item, componentInstance })
        );
      });
    });
  });
}

export default {
  selectConfigOption,
  validateArgumentList,
};
